import React from 'react';
import { Link } from 'react-router-dom';
import { HeartHandshake, Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-secondary-dark text-gray-300">
      <div className="container-custom section-padding pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* About */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center overflow-hidden">
                <img src="/logo.jpeg" alt="Sevasaredham Logo" className="w-full h-full object-cover" />
              </div>
              <span className="text-2xl font-heading font-bold text-white">Sevasaredham</span>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Committed to providing healthcare, education, and community development initiatives throughout India in order to better humanity. Delivering hope where it's most needed.
            </p>
            <div className="flex space-x-3">
              <a href="https://www.facebook.com/sevasaredham" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group">
                <Facebook size={18} className="text-gray-400 group-hover:text-white transition-colors" />
              </a>
              <a href="https://x.com/sevasaredham" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group">
                <Twitter size={18} className="text-gray-400 group-hover:text-white transition-colors" />
              </a>
              <a href="https://www.instagram.com/sevasaredham/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group">
                <Instagram size={18} className="text-gray-400 group-hover:text-white transition-colors" />
              </a>
              <a href="https://youtube.com/@sevasaredham?si=-EUnh05BCUDxkloG" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group">
                {/* Lucide doesn't have a default Youtube icon in the import list, assume I can add it or use a generic one. 
                    Wait, let me check imports. 
                 */}
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 group-hover:text-white transition-colors"><path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17"></path><path d="m10 15 5-3-5-3z"></path></svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white text-lg font-heading font-bold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {[
                ['Become a Volunteer', '/volunteer'],
                ['Request Help', '/help'],
                ['Donate Now', '/donate'],
                ['Events', '/events'],
                ['About Us', '/about']
              ].map(([label, link]) => (
                <li key={link}>
                  <Link to={link} className="hover:text-primary transition-colors hover:translate-x-1 inline-block duration-200">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white text-lg font-heading font-bold mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3 group">
                <div className="mt-1 bg-white/5 p-2 rounded-lg group-hover:bg-primary/20 transition-colors">
                  <Mail size={16} className="text-primary" />
                </div>
                <a href="mailto:info@sevasaredham.org" className="text-gray-400 hover:text-white transition-colors py-1">
                  info@sevasaredham.org
                </a>
              </li>
              <li className="flex items-start space-x-3 group">
                <div className="mt-1 bg-white/5 p-2 rounded-lg group-hover:bg-primary/20 transition-colors">
                  <Phone size={16} className="text-primary" />
                </div>
                <div className="flex flex-col">
                  <a href="tel:+918272873865" className="text-gray-400 hover:text-white transition-colors py-1">
                    +91 82728 73865
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3 group">
                <div className="mt-1 bg-white/5 p-2 rounded-lg group-hover:bg-primary/20 transition-colors">
                  <MapPin size={16} className="text-primary" />
                </div>
                <span className="text-gray-400 py-1">Jaitpur Kalan, Uttar Pradesh 283114</span>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-white text-lg font-heading font-bold mb-6">Newsletter</h3>
            <p className="text-gray-400 mb-4">Subscribe to our newsletter for updates and impact stories.</p>
            <form className="space-y-3">
              <input
                type="email"
                placeholder="Your email address"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none text-white placeholder-gray-500 transition-all"
              />
              <button type="submit" className="w-full btn btn-primary py-3 rounded-xl font-medium">
                Subscribe
              </button>
            </form>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} Sevasaredham. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;